import React, { useState, useEffect, useRef } from 'react';
import {
  getPosts, createPost, updatePost, deletePost, uploadImage,
  getSettings, saveSettings, uploadLogo, uploadLogoCircular,
  getUsers, createUser, updateUser, deleteUser,
  login, logout, isLogged
} from '../api';

// ── LOGIN ─────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    const res = await login(form.username, form.password);
    setLoading(false);
    if (res.token) { localStorage.setItem('admin_token', res.token); onLogin(); }
    else setError(res.error || 'Credenciales incorrectas');
  };

  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg)' }}>
      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:'2.5rem', width:'100%', maxWidth:380 }}>
        <h2 style={{ color:'var(--mustard)', marginBottom:'.4rem', letterSpacing:1 }}>369Admin</h2>
        <p style={{ color:'var(--muted)', fontSize:'.9rem', marginBottom:'1.8rem' }}>Panel de administración</p>
        {error && <div style={{ background:'#3a1a1a', border:'1px solid #6a2d2d', color:'#da7d7d', padding:'.6rem .9rem', borderRadius:6, marginBottom:'1rem', fontSize:'.88rem' }}>{error}</div>}
        <form onSubmit={submit}>
          <div className="form-group">
            <label>Usuario</label>
            <input autoFocus value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Contraseña</label>
            <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
          </div>
          <button type="submit" className="btn btn-primary" style={{ width:'100%', marginTop:'.5rem' }} disabled={loading}>
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── POST MODAL ────────────────────────────────────────────
function PostModal({ post, onClose, onSaved }) {
  const [form, setForm] = useState({ title: '', description: '', video_url: '', images: [] });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (post) setForm({ title: post.title || '', description: post.description || '', video_url: post.video_url || '', images: post.images || [] });
  }, [post]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleImages = async (files) => {
    setUploading(true);
    const urls = [];
    for (const file of files) {
      const res = await uploadImage(file);
      if (res.image_url) urls.push(res.image_url);
    }
    setForm(f => ({ ...f, images: [...f.images, ...urls] }));
    setUploading(false);
  };

  const submit = async () => {
    if (!form.title.trim()) return alert('El título es obligatorio');
    if (post?.id) await updatePost(post.id, form);
    else await createPost(form);
    onSaved(); onClose();
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h2>{post?.id ? 'Editar publicación' : 'Nueva publicación'}</h2>
        <div className="form-group">
          <label>Título *</label>
          <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="Título" />
        </div>
        <div className="form-group">
          <label>Descripción</label>
          <textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="Descripción..." />
        </div>
        <div className="form-group">
          <label>URL de video (YouTube / Vimeo)</label>
          <input value={form.video_url} onChange={e => set('video_url', e.target.value)} placeholder="https://youtube.com/watch?v=..." />
        </div>
        <div className="form-group">
          <label>Galería de fotos</label>
          <input type="file" ref={fileRef} multiple accept="image/*" style={{ display:'none' }} onChange={e => handleImages(Array.from(e.target.files))} />
          <button className="btn btn-outline btn-sm" onClick={() => fileRef.current.click()} disabled={uploading}>
            {uploading ? 'Subiendo...' : '+ Agregar fotos'}
          </button>
          <div className="img-preview-list">
            {form.images.map((url, i) => (
              <div key={i} className="img-preview-item">
                <img src={url} alt={`img-${i}`} />
                <button onClick={() => setForm(f => ({ ...f, images: f.images.filter((_, j) => j !== i) }))}>×</button>
              </div>
            ))}
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={submit}>Guardar</button>
        </div>
      </div>
    </div>
  );
}

// ── USER MODAL ────────────────────────────────────────────
function UserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({ username: '', password: '', role: 'editor' });
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.id) setForm({ username: user.username, password: '', role: user.role });
  }, [user]);

  const submit = async () => {
    if (!form.username.trim()) return setError('El usuario es obligatorio');
    if (!user?.id && !form.password.trim()) return setError('La contraseña es obligatoria');
    const res = user?.id ? await updateUser(user.id, form) : await createUser(form);
    if (res.error) return setError(res.error);
    onSaved(); onClose();
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h2>{user?.id ? 'Editar usuario' : 'Nuevo usuario'}</h2>
        {error && <div style={{ color:'#da7d7d', fontSize:'.88rem', marginBottom:'1rem' }}>{error}</div>}
        <div className="form-group">
          <label>Usuario *</label>
          <input value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} placeholder="nombre de usuario" />
        </div>
        <div className="form-group">
          <label>{user?.id ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña *'}</label>
          <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
        </div>
        <div className="form-group">
          <label>Rol</label>
          <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
            style={{ width:'100%', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:6, color:'var(--text)', padding:'.6rem .8rem', fontSize:'.95rem' }}>
            <option value="admin">Admin</option>
            <option value="editor">Editor</option>
          </select>
        </div>
        <div className="modal-actions">
          <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={submit}>Guardar</button>
        </div>
      </div>
    </div>
  );
}

// ── SETTINGS ──────────────────────────────────────────────
function SettingsPanel({ onSaved }) {
  const [form, setForm] = useState({ site_title: '', logo_url: '', logo_circular_url: '', logo_size: 52 });
  const [saving, setSaving] = useState(false);
  const logoRef = useRef();
  const logoCircRef = useRef();

  useEffect(() => {
    getSettings().then(s => setForm({
      site_title: s.site_title || '',
      logo_url: s.logo_url || '',
      logo_circular_url: s.logo_circular_url || '',
      logo_size: parseInt(s.logo_size) || 52,
    })).catch(() => {});
  }, []);

  const handleLogo = async (file) => {
    const res = await uploadLogo(file);
    if (res.logo_url) setForm(f => ({ ...f, logo_url: res.logo_url }));
  };

  const handleLogoCircular = async (file) => {
    const res = await uploadLogoCircular(file);
    if (res.logo_circular_url) setForm(f => ({ ...f, logo_circular_url: res.logo_circular_url }));
  };

  const submit = async () => {
    setSaving(true);
    await saveSettings(form);
    setSaving(false);
    onSaved();
    alert('Configuración guardada');
  };

  return (
    <div style={{ maxWidth: 520 }}>
      <div className="section-header"><h2>Configuración del sitio</h2></div>
      <div className="form-group">
        <label>Título del sitio</label>
        <input value={form.site_title} onChange={e => setForm(f => ({ ...f, site_title: e.target.value }))} />
      </div>
      <div className="form-group">
        <label>Logo principal (navbar)</label>
        <div style={{ display:'flex', alignItems:'center', gap:'1rem', marginTop:'.4rem' }}>
          <button className="btn btn-outline btn-sm" onClick={() => logoRef.current.click()}>Subir logo</button>
          <input type="file" ref={logoRef} accept="image/*" style={{ display:'none' }} onChange={e => handleLogo(e.target.files[0])} />
          {form.logo_url && <img src={form.logo_url} alt="logo" style={{ height:`${form.logo_size}px`, width:'auto', maxWidth:180, objectFit:'contain', border:'1px solid var(--border)', borderRadius:4 }} />}
        </div>
      </div>
      <div className="form-group">
        <label>Tamaño del logo: <strong style={{ color:'var(--mustard)' }}>{form.logo_size}px</strong></label>
        <input type="range" min="24" max="120" step="4" value={form.logo_size}
          onChange={e => setForm(f => ({ ...f, logo_size: parseInt(e.target.value) }))}
          style={{ width:'100%', accentColor:'var(--mustard)', marginTop:'.4rem' }} />
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:'.75rem', color:'var(--muted)' }}><span>24px</span><span>120px</span></div>
      </div>
      <div className="form-group">
        <label>Logo circular (avatar del perfil)</label>
        <div style={{ display:'flex', alignItems:'center', gap:'1rem', marginTop:'.4rem' }}>
          <button className="btn btn-outline btn-sm" onClick={() => logoCircRef.current.click()}>Subir logo circular</button>
          <input type="file" ref={logoCircRef} accept="image/*" style={{ display:'none' }} onChange={e => handleLogoCircular(e.target.files[0])} />
          {form.logo_circular_url && <img src={form.logo_circular_url} alt="circular" style={{ width:64, height:64, borderRadius:'50%', objectFit:'cover', border:'2px solid var(--mustard)' }} />}
        </div>
      </div>
      <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? 'Guardando...' : 'Guardar cambios'}</button>
    </div>
  );
}

// ── ADMIN PRINCIPAL ───────────────────────────────────────
export default function Admin({ onSettingsChange }) {
  const [logged, setLogged] = useState(isLogged());
  const [tab, setTab]       = useState('posts');
  const [posts, setPosts]   = useState([]);
  const [users, setUsers]   = useState([]);
  const [postModal, setPostModal] = useState(null);
  const [userModal, setUserModal] = useState(null);

  const loadPosts = () => getPosts().then(setPosts).catch(() => {});
  const loadUsers = () => getUsers().then(r => Array.isArray(r) ? setUsers(r) : null).catch(() => {});

  useEffect(() => { if (logged) { loadPosts(); loadUsers(); } }, [logged]);

  const handleLogout = () => { logout(); setLogged(false); };

  const handleDeletePost = async (id) => {
    if (!window.confirm('¿Eliminar esta publicación?')) return;
    await deletePost(id); loadPosts();
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('¿Eliminar este usuario?')) return;
    await deleteUser(id); loadUsers();
  };

  if (!logged) return <LoginScreen onLogin={() => setLogged(true)} />;

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div style={{ color:'var(--mustard)', fontWeight:700, fontSize:'1rem', padding:'.6rem .8rem', marginBottom:'.5rem', letterSpacing:1 }}>369Admin</div>
        <button className={tab === 'posts' ? 'active' : ''} onClick={() => setTab('posts')}>📋 Publicaciones</button>
        <button className={tab === 'users' ? 'active' : ''} onClick={() => setTab('users')}>👥 Usuarios</button>
        <button className={tab === 'settings' ? 'active' : ''} onClick={() => setTab('settings')}>⚙️ Configuración</button>
        <button onClick={handleLogout} style={{ marginTop:'auto', color:'#da7d7d' }}>🚪 Cerrar sesión</button>
      </aside>

      <main className="admin-content">

        {/* PUBLICACIONES */}
        {tab === 'posts' && (
          <>
            <div className="section-header">
              <h2>Publicaciones</h2>
              <button className="btn btn-primary" onClick={() => setPostModal({})}>+ Nueva</button>
            </div>
            {posts.length === 0 ? (
              <div className="empty-state">No hay publicaciones aún.</div>
            ) : (
              <table className="admin-table">
                <thead><tr><th>Título</th><th>Tipo</th><th>Fecha</th><th>Acciones</th></tr></thead>
                <tbody>
                  {posts.map(p => (
                    <tr key={p.id}>
                      <td>{p.title}</td>
                      <td><span className={`badge ${p.video_url ? 'badge-video' : 'badge-gallery'}`}>{p.video_url ? 'Video' : `Galería (${p.images?.length || 0})`}</span></td>
                      <td>{new Date(p.created_at).toLocaleDateString('es-ES')}</td>
                      <td style={{ display:'flex', gap:'.5rem' }}>
                        <button className="btn btn-outline btn-sm" onClick={() => setPostModal(p)}>Editar</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeletePost(p.id)}>Eliminar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {/* USUARIOS */}
        {tab === 'users' && (
          <>
            <div className="section-header">
              <h2>Usuarios</h2>
              <button className="btn btn-primary" onClick={() => setUserModal({})}>+ Nuevo</button>
            </div>
            {users.length === 0 ? (
              <div className="empty-state">No hay usuarios.</div>
            ) : (
              <table className="admin-table">
                <thead><tr><th>Usuario</th><th>Rol</th><th>Creado</th><th>Acciones</th></tr></thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td>{u.username}</td>
                      <td><span className={`badge ${u.role === 'admin' ? 'badge-video' : 'badge-gallery'}`}>{u.role}</span></td>
                      <td>{new Date(u.created_at).toLocaleDateString('es-ES')}</td>
                      <td style={{ display:'flex', gap:'.5rem' }}>
                        <button className="btn btn-outline btn-sm" onClick={() => setUserModal(u)}>Editar</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteUser(u.id)}>Eliminar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {tab === 'settings' && <SettingsPanel onSaved={onSettingsChange} />}
      </main>

      {postModal !== null && <PostModal post={postModal} onClose={() => setPostModal(null)} onSaved={loadPosts} />}
      {userModal !== null && <UserModal user={userModal} onClose={() => setUserModal(null)} onSaved={loadUsers} />}
    </div>
  );
}
